// stores/offline/OfflineQueueManager.ts
import localforage from 'localforage';
import { makeAutoObservable, runInAction } from 'mobx';

export interface QueuedOperation {
    id: string;
    storeName: string;
    method: 'create' | 'update' | 'patch' | 'remove';
    entityId?: string;
    data?: any;
    timestamp: number;
    retryCount: number;
    conflictResolution?: 'client' | 'server' | 'merge' | 'manual';
}

export class OfflineQueueManager {
    private queue: Map<string, QueuedOperation> = new Map();
    private storage: LocalForage;
    isProcessing = false;
    failedOperations: QueuedOperation[] = [];

    constructor() {
        makeAutoObservable(this);

        this.storage = localforage.createInstance({
            name: 'offlineQueue',
            storeName: 'operations'
        });

        this.loadQueue();
    }

    private async loadQueue() {
        const keys = await this.storage.keys();
        const operations = await Promise.all(
            keys.map(key => this.storage.getItem<QueuedOperation>(key))
        );

        runInAction(() => {
            operations.forEach(op => {
                if (op) this.queue.set(op.id, op);
            });
        });
    }

    async enqueue(operation: Omit<QueuedOperation, 'id' | 'timestamp' | 'retryCount'>) {
        const op: QueuedOperation = {
            ...operation,
            id: `${operation.storeName}_${operation.method}_${Date.now()}_${Math.random()}`,
            timestamp: Date.now(),
            retryCount: 0
        };

        await this.storage.setItem(op.id, op);

        runInAction(() => {
            this.queue.set(op.id, op);
        });

        return op.id;
    }

    async dequeue(id: string) {
        await this.storage.removeItem(id);

        runInAction(() => {
            this.queue.delete(id);
        });
    }

    async processQueue(
        executor: (op: QueuedOperation) => Promise<boolean>,
        onConflict?: (op: QueuedOperation, serverData: any) => Promise<any>
    ) {
        if (this.isProcessing) return;

        runInAction(() => {
            this.isProcessing = true;
            this.failedOperations = [];
        });

        const operations = Array.from(this.queue.values())
            .sort((a, b) => a.timestamp - b.timestamp);

        for (const op of operations) {
            try {
                const success = await executor(op);

                if (success) {
                    await this.dequeue(op.id);
                } else {
                    op.retryCount++;

                    if (op.retryCount > 3) {
                        runInAction(() => {
                            this.failedOperations.push(op);
                        });
                        await this.dequeue(op.id);
                    } else {
                        await this.storage.setItem(op.id, op);
                    }
                }
            } catch (error: any) {
                if (error.code === 409 && onConflict) {
                    // Handle conflict
                    const resolved = await onConflict(op, error.serverData);
                    if (resolved) {
                        await this.dequeue(op.id);
                    }
                } else {
                    console.error(`Failed to process operation ${op.id}:`, error);
                    runInAction(() => {
                        this.failedOperations.push(op);
                    });
                }
            }
        }

        runInAction(() => {
            this.isProcessing = false;
        });
    }

    getQueuedOperations(storeName?: string): QueuedOperation[] {
        const ops = Array.from(this.queue.values());
        return storeName
            ? ops.filter(op => op.storeName === storeName)
            : ops;
    }

    async clearQueue() {
        await this.storage.clear();
        runInAction(() => {
            this.queue.clear();
            this.failedOperations = [];
        });
    }

    get queueSize() {
        return this.queue.size;
    }

    get hasQueuedOperations() {
        return this.queue.size > 0;
    }
}